import { fetchApi } from "@/lib/api/index.ts";
import { useToast } from "@/components/ui/use-toast.ts";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

export interface CampaignCode {
  id: string;
  code: string;
  slots: number;
  description: string | null;
  expiration_time: string | null;
  metadata: Record<string, unknown>;
  enabled: boolean;
  created_at: string;
  updated_at: string;
  redeemed_count?: number;
}

export interface CampaignCodeAssignment {
  id: string;
  user_id: string;
  campaign_code_id: string;
  created_at: string;
  updated_at: string;
}

export interface CampaignCodeUser {
  user_id: string;
  name: string;
  email: string;
  phone: string;
  role: string;
  created_at: string;
  assigned_groups: Array<{ id: string; name: string }>;
  total_messages_sent: string;
  days_active: string;
  last_message_date: string | null;
  assigned_at: string;
  deactivated: boolean;
  sdk_url: string | null;
  mentorship_id: string | null;
}

export const useCampaignCodes = () => {
  return useQuery({
    queryKey: ["campaign-codes"],
    queryFn: async () => {
      const { data } = await fetchApi<{ campaign_codes: CampaignCode[] }>({
        url: "/v2/campaign-codes",
        method: "GET",
      });
      return data?.campaign_codes ?? [];
    },
  });
};

export const useCampaignCode = (id: string) => {
  const queryClient = useQueryClient();
  
  return useQuery({
    queryKey: ["campaign-code", id],
    queryFn: async () => {
      const { data } = await fetchApi<CampaignCode | { campaign_code: CampaignCode }>({
        url: `/v2/campaign-codes/${id}`,
        method: "GET",
      });
      
      if (data && 'campaign_code' in data) {
        return data.campaign_code;
      }
      return data as CampaignCode;
    },
    enabled: !!id,
    initialData: () => {
      const cachedList = queryClient.getQueryData<CampaignCode[]>(["campaign-codes"]);
      return cachedList?.find(c => c.id === id);
    },
    staleTime: 30000,
  });
};

export interface CreateCampaignCodeInput {
  code: string;
  slots: number;
  description?: string;
}

export const useCreateCampaignCode = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (input: CreateCampaignCodeInput) => {
      const { data } = await fetchApi<{ campaign_code: CampaignCode }>({
        url: "/v2/campaign-codes/create",
        method: "POST",
        data: input,
      });
      return data?.campaign_code;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaign-codes"] });
      toast({
        title: "Campaign code created",
        description: "The campaign code has been created successfully.",
      });
    },
    onError: () => {
      toast({
        title: "Failed to create campaign code",
        description: "Please try again.",
        variant: "destructive",
      });
    },
  });
};

export interface UpdateCampaignCodeInput {
  campaign_code_id: string;
  slots?: number;
  description?: string;
  enabled?: boolean;
}

export const useUpdateCampaignCode = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ campaign_code_id, ...input }: UpdateCampaignCodeInput) => {
      const { data } = await fetchApi<{ campaign_code: CampaignCode }>({
        url: `/v2/campaign-codes/${campaign_code_id}`,
        method: "PUT",
        data: input,
      });
      return data?.campaign_code;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["campaign-codes"] });
      queryClient.invalidateQueries({ queryKey: ["campaign-code", variables.campaign_code_id] });
      toast({
        title: "Campaign code updated",
        description: "The campaign code has been updated successfully.",
      });
    },
    onError: () => {
      toast({
        title: "Failed to update campaign code",
        description: "Please try again.",
        variant: "destructive",
      });
    },
  });
};

export interface CampaignCodeUsersResponse {
  users: CampaignCodeUser[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export const useCampaignCodeUsers = (
  campaignCodeId: string,
  page: number = 1,
  limit: number = 2
) => {
  return useQuery({
    queryKey: ["campaign-code-users", campaignCodeId, page, limit],
    queryFn: async () => {
      const { data } = await fetchApi<CampaignCodeUsersResponse>({
        url: `/v2/campaign-codes/${campaignCodeId}/users?page=${page}&limit=${limit}`,
        method: "GET",
      });
      return data;
    },
    enabled: !!campaignCodeId,
  });
};

export const useDeleteCampaignCode = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (campaignCodeId: string) => {
      await fetchApi({
        url: `/v2/campaign-codes/${campaignCodeId}`,
        method: "DELETE",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaign-codes"] });
      toast({
        title: "Campaign code deleted",
        description: "The campaign code has been deleted successfully.",
      });
    },
    onError: () => {
      toast({
        title: "Failed to delete campaign code",
        description: "Please try again.",
        variant: "destructive",
      });
    },
  });
};
